/** Days before expiry when renewal is offered (must match portal-backend). */
export const RENEWAL_WINDOW_DAYS = 3

export const daysUntilExpiry = subscription => {
  const expiryDate =
    subscription?.expiresAt ||
    subscription?.currentPeriodEnd ||
    subscription?.endDate
  if (!expiryDate) return null
  const diff = new Date(expiryDate) - new Date()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

/**
 * Client-side renewal eligibility (mirrors backend rules).
 */
export const getRenewalEligibility = (subscription, plan = null) => {
  if (!subscription) {
    return { eligible: false, reason: "no_subscription" }
  }

  const status = String(subscription.status || "").toLowerCase()
  if (status === "cancelled") {
    return { eligible: false, reason: "cancelled" }
  }

  const resolvedPlan = plan || subscription.plan || subscription.planId || null
  const price = Number(
    resolvedPlan?.price ?? subscription.amount ?? subscription.planAmount ?? 0
  )
  const days = daysUntilExpiry(subscription)

  if (status === "active") {
    if (days === null) {
      return { eligible: true, reason: "active", requiresPayment: price > 0, daysUntilExpiry: days }
    }
    if (days <= 0) {
      return { eligible: true, reason: "expired", requiresPayment: price > 0, daysUntilExpiry: days }
    }
    if (days <= RENEWAL_WINDOW_DAYS) {
      return {
        eligible: true,
        reason: "expiring_soon",
        requiresPayment: price > 0,
        daysUntilExpiry: days,
      }
    }
    return {
      eligible: false,
      reason: "not_due_yet",
      requiresPayment: price > 0,
      daysUntilExpiry: days,
    }
  }

  if (status === "inactive" || status === "suspended") {
    return {
      eligible: true,
      reason: "reactivate",
      requiresPayment: price > 0,
      daysUntilExpiry: days,
    }
  }

  return { eligible: false, reason: "unsupported_status", daysUntilExpiry: days }
}

/**
 * Whether cancel is allowed from the billing dashboard (active subscription only).
 */
export const getCancellationEligibility = subscription => {
  if (!subscription) {
    return { eligible: false, reason: "not_found" }
  }
  const status = String(subscription.status || "").toLowerCase()
  if (status === "cancelled") {
    return { eligible: false, reason: "already_cancelled" }
  }
  if (subscription.cancelAtPeriodEnd) {
    return {
      eligible: false,
      reason: "cancel_scheduled",
      effectiveEnd:
        subscription.currentPeriodEnd ||
        subscription.expiresAt ||
        subscription.endDate ||
        null,
    }
  }
  if (status !== "active") {
    return { eligible: false, reason: "not_active" }
  }
  return {
    eligible: true,
    reason: "active",
    effectiveEnd:
      subscription.currentPeriodEnd ||
      subscription.expiresAt ||
      subscription.endDate ||
      null,
  }
}

export const resolvePaymentUrl = data => {
  if (!data) return ""
  if (data.paymentUrl) return data.paymentUrl
  const session = data.session
  if (!session || typeof session !== "object") return ""
  return (
    session.paymentUrl ||
    session.payment_url ||
    session.checkout_url ||
    session.redirect_url ||
    session.url ||
    session.transaction?.url ||
    ""
  )
}
